import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, BrowserRouter as Router } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import SEO from "@/components/SEO";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute.jsx";
import { AuthProvider } from "@/contexts/AuthContext.jsx";
import { ListingAuthProvider } from "@/contexts/ListingAuthContext.jsx";
import ListingProtectedRoute from "@/components/ListingProtectedRoute.jsx";

const CallbackWidget = lazy(() => import("@/components/CallbackWidget"));
const HomePage = lazy(() => import("./pages/HomePage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const PropertyDetailPage = lazy(() => import("./pages/PropertyDetailPage"));
const GreaterNoidaRentPage = lazy(() => import("./pages/GreaterNoidaRentPage"));
const AgentsPage = lazy(() => import("./pages/AgentsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ListPropertyLoginPage = lazy(() => import("./pages/ListPropertyLoginPage"));
const ListPropertyVerifyOTPPage = lazy(() => import("./pages/ListPropertyVerifyOTPPage"));
const ListPropertyFormPage = lazy(() => import("./pages/ListPropertyFormPage"));
const ListPropertySuccessPage = lazy(() => import("./pages/ListPropertySuccessPage"));
const OwnerPortfolioPage = lazy(() => import("./pages/OwnerPortfolioPage"));
const OwnerTenantDetailsPage = lazy(() => import("./pages/OwnerTenantDetailsPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AddPropertyForm = lazy(() => import("./pages/admin/AddPropertyForm"));
const AddSocietyPage = lazy(() => import("./pages/admin/AddSocietyPage"));
const BookUnitForm = lazy(() => import("./pages/admin/BookUnitForm"));
const BookingDetailsPage = lazy(() => import("./pages/admin/BookingDetailsPage"));
const EditPropertyForm = lazy(() => import("./pages/admin/EditPropertyForm"));
const AdminUserPage = lazy(() => import("./pages/admin/AdminUserPage"));
const AdminContactLeadsPage = lazy(() => import("./pages/admin/AdminContactLeadsPage"));
const AdminListingLeadsPage = lazy(() => import("./pages/admin/AdminListingLeadsPage"));
const AdminVisitLeadsPage = lazy(() => import("./pages/admin/AdminVisitLeadsPage"));

function App({ RouterWrapper = Router, helmetContext = {}, initialData = {} } = {}) {

  return (
    <HelmetProvider context={helmetContext}>
    <AuthProvider>
      <ListingAuthProvider>
        <RouterWrapper>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-[50vh]" aria-live="polite" />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/properties" element={<PropertiesPage />} />
              <Route path="/properties/:id" element={<PropertyDetailPage />} />
              <Route
                path="/flats-for-rent/greater-noida"
                element={
                  <GreaterNoidaRentPage
                    initialData={initialData.greaterNoidaRentPage}
                  />
                }
              />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />

            {/* Property Listing Routes */}
            <Route path="/login" element={<ListPropertyLoginPage />} />
            <Route
              path="/list-property/login"
              element={<ListPropertyLoginPage />}
            />
            <Route
              path="/list-property/verify-otp"
              element={<ListPropertyVerifyOTPPage />}
            />
            <Route
              path="/list-property/form"
              element={
                <ListingProtectedRoute>
                  <ListPropertyFormPage />
                </ListingProtectedRoute>
              }
            />
            <Route
              path="/owner/portfolio"
              element={
                <ListingProtectedRoute>
                  <OwnerPortfolioPage />
                </ListingProtectedRoute>
              }
            />
            <Route
              path="/owner/tenant-details/:propertyId"
              element={
                <ListingProtectedRoute>
                  <OwnerTenantDetailsPage />
                </ListingProtectedRoute>
              }
            />
            <Route
              path="/tenant/portfolio"
              element={
                <ListingProtectedRoute>
                  <OwnerTenantDetailsPage />
                </ListingProtectedRoute>
              }
            />
            <Route
              path="/owner/dashboard"
              element={<Navigate to="/owner/portfolio" replace />}
            />
            <Route
              path="/owner/dashbord"
              element={<Navigate to="/owner/portfolio" replace />}
            />
            <Route
              path="/list-property/success"
              element={<ListPropertySuccessPage />}
            />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedAdminRoute>
                  <AdminDashboard />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/add-property"
              element={
                <ProtectedAdminRoute>
                  <AddPropertyForm />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/add-society"
              element={
                <ProtectedAdminRoute>
                  <AddSocietyPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/user"
              element={
                <ProtectedAdminRoute>
                  <AdminUserPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/listing-leads"
              element={
                <ProtectedAdminRoute>
                  <AdminListingLeadsPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/contact-leads"
              element={
                <ProtectedAdminRoute>
                  <AdminContactLeadsPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/visit-leads"
              element={
                <ProtectedAdminRoute>
                  <AdminVisitLeadsPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/edit-property/:id"
              element={
                <ProtectedAdminRoute>
                  <EditPropertyForm />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/book-unit/:propertyId"
              element={
                <ProtectedAdminRoute>
                  <BookUnitForm />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/booking-details/:tenantId"
              element={
                <ProtectedAdminRoute>
                  <BookingDetailsPage />
                </ProtectedAdminRoute>
              }
            />

            {/* 404 Route */}
            <Route
              path="*"
              element={
                <>
                  <SEO
                    title="Page Not Found | OmNivaas Properties"
                    description="The requested OmNivaas Properties page could not be found."
                    path="/404"
                    robots="noindex,nofollow"
                  />
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold mb-4">404</h1>
                      <p className="text-xl text-muted-foreground mb-8">
                        Page not found
                      </p>
                      <a href="/" className="text-primary hover:underline">
                        Back to home
                      </a>
                    </div>
                  </div>
                </>
              }
            />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <CallbackWidget />
          </Suspense>
          <Toaster />
        </RouterWrapper>
      </ListingAuthProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
